import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import type { Role } from "../api/client";
import { PageLoader } from "../components/BrandedSpinner";
import { RESETDATA_MASTER, STATUS_LABEL } from "../lib/role-labels";
import { useCurrentUser } from "./useCurrentUser";

interface Props {
  children: React.ReactNode;
  /** Allowed roles. Empty array means "any authenticated active user". */
  roles?: Role[];
  /**
   * CHP-446 C1: restrict to ResetData Masters even within the allowed roles.
   * Fail-closed — a null master flag (the JWT first-contact path) is treated as
   * not-Master, mirroring the backend admin-tier guard. Used to keep a
   * constrained RA out of the RM-only Admin Hub at the route level; the nav link
   * is already RM-only (RACCPE-17), this closes the by-URL hole. Wiring is kept
   * intact so re-enabling RA later is a one-prop change.
   */
  requireMaster?: boolean;
}

export function ProtectedRoute({ children, roles, requireMaster }: Props) {
  const { data, isLoading, error, isAuthenticated, isAuthLoading } = useCurrentUser();
  const navigate = useNavigate();

  // CHP-1105: resolve the single redirect destination as a value instead of
  // returning a render-time <Navigate>. A render-time <Navigate> re-navigates
  // on every re-render the transition itself causes (the CHP-1203 "Maximum
  // update depth exceeded" loop) and flashes a frame of the wrong route before
  // it resolves. Deriving one target + firing it from an effect (below)
  // navigates exactly once and shows a loader until it lands — no flicker.
  //
  // `null` means "no redirect" — either not decidable yet (auth/me loading) or
  // this user stays put. The three inline informational states (me-fetch error,
  // deactivated, pending) are NOT redirects; they contribute `null` here and are
  // rendered in place below. Decision order is preserved exactly from the
  // pre-CHP-1105 sequential guards:
  //   loading → unauth → error → inactive → pending → T&Cs → re-accept → role → master
  const redirectTarget: string | null =
    isAuthLoading || isLoading
      ? null
      : // ADR-0010: unauthenticated users land on PRM's /login (the PRM-rendered
        // form), not Authentik. The form drives Authentik via the flow proxy.
        !isAuthenticated
        ? "/login"
        : error || !data
          ? null
          : !data.is_active
            ? null
            : // Slice 4c: gate on data.status, not data.role. Pending users have
              // role=null + status='pending'.
              data.status === "pending"
              ? null
              : // T&Cs gate: a non-pending user without an acceptance timestamp
                // must walk the onboarding wizard first. The /onboarding/* routes
                // are mounted OUTSIDE ProtectedRoute, so this doesn't recurse.
                data.tc_accepted_at == null
                ? "/onboarding/identity"
                : // CHP-1154: accepted, but under an older terms version → the
                  // re-acceptance step. /onboarding/terms handles the stale case
                  // without the CHP-1021 non-signer redirect.
                  data.tc_reaccept_required
                  ? "/onboarding/terms"
                  : // Slice 4c: a null role past the pending gate is an unexpected
                    // state — treat as forbidden.
                    roles && roles.length > 0 && (data.role === null || !roles.includes(data.role))
                    ? "/forbidden"
                    : // CHP-446 C1: Master-only route gate. Mirrors the backend
                      // admin-tier guard — an RA (is_resetdata_master === false)
                      // or a null-master admin is forbidden even though their
                      // "admin" role is in the allowed list above.
                      requireMaster && data.is_resetdata_master !== true
                      ? "/forbidden"
                      : null;

  // Fire the redirect imperatively, exactly once (ref-guarded so StrictMode's
  // double-invoke and any interim re-render can't re-fire it). `replace` keeps
  // the rejected location out of history — matches the CHP-1203 login gate.
  const redirected = useRef(false);
  useEffect(() => {
    if (redirectTarget === null || redirected.current) return;
    redirected.current = true;
    void navigate({ to: redirectTarget, replace: true });
  }, [redirectTarget, navigate]);

  if (isAuthLoading || isLoading) {
    return <PageLoader />;
  }

  // A pending redirect renders the loader (not the guarded children, not a
  // flash of the wrong route) until the effect above navigates away.
  if (redirectTarget !== null) {
    return <PageLoader />;
  }

  if (error || !data) {
    return <div role="alert">Could not load your account. Try refreshing.</div>;
  }

  if (!data.is_active) {
    return (
      <div role="alert">
        <h2>Your account has been deactivated</h2>
        <p>Contact your {RESETDATA_MASTER} if this is unexpected.</p>
      </div>
    );
  }

  if (data.status === "pending") {
    return (
      <div role="status">
        <h2>{STATUS_LABEL.pending}</h2>
        <p>A {RESETDATA_MASTER} will review your access shortly.</p>
      </div>
    );
  }

  return <>{children}</>;
}
